import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button } from '@structural-climbing/ui'
import type { GymSection } from './types'
import { AddSectionModal } from './AddSectionModal'
import { EditSectionModal } from './EditSectionModal'
import { GymMapUpload } from './GymMapUpload'

interface SectionsTabProps {
  sections: GymSection[]
  /** Section discipline new sections are created with (from the active tab). */
  newSectionDiscipline?: string
  onRefresh: () => void
  apiUrl: string
}

const DISCIPLINE_KEYS: Record<string, string> = {
  bouldering: 'routes.disciplineBouldering',
  rope: 'routes.disciplineRope',
  other: 'routes.disciplineOther',
}

export function SectionsTab({
  sections,
  newSectionDiscipline = 'bouldering',
  onRefresh,
  apiUrl,
}: SectionsTabProps) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()

  const [addOpen, setAddOpen] = useState(false)
  const [editSection, setEditSection] = useState<GymSection | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({})

  async function handleDelete(section: GymSection) {
    setDeletingId(section.id)
    setDeleteErrors((prev) => {
      const next = { ...prev }
      delete next[section.id]
      return next
    })
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-sections/${section.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const json = (await res.json()) as { error?: string }
        const msg =
          json.error === 'sectionHasRoutes'
            ? t('routes.sectionHasRoutes')
            : (json.error ?? t('routes.errorDeleteSection'))
        setDeleteErrors((prev) => ({ ...prev, [section.id]: msg }))
        return
      }
      onRefresh()
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <>
      <GymMapUpload />

      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground/60">{t('routes.sections')}</p>
        <Button color="primary" size="sm" onClick={() => setAddOpen(true)}>
          {t('routes.addSection')}
        </Button>
      </div>

      {sections.length === 0 ? (
        <p className="py-12 text-center text-sm text-foreground/40">{t('routes.noSections')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {sections.map((section) => (
            <div
              key={section.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-divider bg-content1 px-3 py-2"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className="truncate font-medium">{section.name}</span>
                {section.discipline && (
                  <span className="flex-none rounded-full bg-default-100 px-2 py-0.5 text-xs text-foreground/60">
                    {t(DISCIPLINE_KEYS[section.discipline] ?? 'routes.disciplineOther')}
                  </span>
                )}
              </div>
              <div className="flex flex-none items-center gap-1">
                <Button size="sm" variant="light" onClick={() => setEditSection(section)}>
                  {t('routes.edit')}
                </Button>
                <Button
                  isIconOnly
                  aria-label={t('routes.deleteSection')}
                  size="sm"
                  variant="light"
                  color="danger"
                  isLoading={deletingId === section.id}
                  isDisabled={deletingId === section.id}
                  onClick={() => void handleDelete(section)}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </Button>
              </div>
              {deleteErrors[section.id] && (
                <p className="w-full text-xs text-danger">{deleteErrors[section.id]}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <AddSectionModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          onRefresh()
          setAddOpen(false)
        }}
        apiUrl={apiUrl}
        discipline={newSectionDiscipline}
      />

      <EditSectionModal
        section={editSection}
        onClose={() => setEditSection(null)}
        onSaved={() => {
          onRefresh()
          setEditSection(null)
        }}
        apiUrl={apiUrl}
      />
    </>
  )
}
